import { createApi } from "@reduxjs/toolkit/query/react";
import { GeneralPayloadType } from "@src/pages/quiz/create-quiz-drawer/task-manager/types&validations";
import { CreateUpdateAdditionalDetailsFormPayload } from "@src/pages/task-manager/additional-details-form/AdditionalDetailsForm.types";
import { InviteResponseData } from "@src/pages/task-manager/quiz/invite-people-drawer/types";
import {
  AssingedQuiz,
  TaskManagerQuizResponse,
} from "@src/pages/task-manager/quiz/quiz-table/types";

import { APIResponse, QuizOrTask } from "@src/types/CommonTypes";
import { baseQueryWithReauth } from "@src/utils/baseQuery";

export interface GetQuizForOrganizationResponse extends APIResponse {
  response: TaskManagerQuizResponse;
}

export interface AssignTaskManagerQuizResponse extends APIResponse {
  response: InviteResponseData;
}

export interface GetAssingedTaskManagerQuizResponse extends APIResponse {
  response: AssingedQuiz[];
}

export interface GetQuizSummaryResponse extends APIResponse {
  response: QuizOrTask;
}

export interface GetDuplicatedQuizResponse extends APIResponse {
  response: boolean;
}

export interface GetQuizByIdResponse extends APIResponse {
  response: QuizOrTask;
}
export interface CreateTaskManagerQuizResponse extends APIResponse {
  response: {
    id: string;
  };
}

export interface UpdateTaskManagerQuizResponse extends APIResponse {
  response: {
    id: string;
  };
}

export interface CreateUpdateAdditionalDetailsFormResponse extends APIResponse {
  response: QuizOrTask;
}

export type AddUpdateStartEndDateResponse = APIResponse;

export type UpdateTaskManagerQuizActionResponse = APIResponse;

export const TaskManagerQuizApiSlice = createApi({
  reducerPath: "QuizApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["getSuperTaskManagerQuiz"],
  endpoints: (builder) => ({
    getSuperTaskManagerQuiz: builder.query<
      GetQuizForOrganizationResponse,
      {
        pageNo: number;
        pageSize: number;
        year: number;
        month: number;
        organizationId: string;
      }
    >({
      query: ({ pageNo, pageSize, year, month, organizationId }) => ({
        url: `quiz/get-organization-quizzes?pageNo=${pageNo}&pageSize=${pageSize}&year=${year}&month=${month}&organizationId=${organizationId}`,
        method: "GET",
      }),
      providesTags: ["getSuperTaskManagerQuiz"],
    }),
    addUpdateStartEndDate: builder.mutation<
      AddUpdateStartEndDateResponse,
      { quizId: string; startDate: string; endDate: string }
    >({
      query: ({ startDate, endDate, quizId }) => ({
        url: `quiz`,
        method: "PATCH",
        params: { startDate, endDate, quizId },
      }),
    }),
    getQuizByID: builder.query<GetQuizByIdResponse, { quizId: string }>({
      query: ({ quizId }) => ({
        url: `quiz/get?quizId=${quizId}`,
        method: "GET",
      }),
    }),
    createQuiz: builder.mutation<
      CreateTaskManagerQuizResponse,
      { payload: GeneralPayloadType | null; organizationId?: string }
    >({
      query: ({ payload, organizationId }) => {
        const url = organizationId
          ? `quiz?organizationId=${organizationId}`
          : `quiz`;
        return {
          url,
          method: "POST",
          body: payload,
        };
      },
    }),

    updateQuiz: builder.mutation<
      UpdateTaskManagerQuizResponse,
      {
        payload: GeneralPayloadType | null;
        quizId: string;
        organizationId?: string;
      }
    >({
      query: ({ payload, quizId, organizationId }) => {
        const url = organizationId
          ? `quiz?quizId=${quizId}&organizationId=${organizationId}`
          : `quiz?quizId=${quizId}`;
        return {
          url,
          method: "PUT",
          body: payload,
        };
      },
    }),
    updateQuizAction: builder.mutation<
      UpdateTaskManagerQuizActionResponse,
      { quizId: string; quizStatus: string }
    >({
      query: ({ quizId, quizStatus }) => ({
        url: `quiz/update-quiz-status?quizId=${quizId}&quizStatus=${quizStatus}`,
        method: "PATCH",
      }),
    }),
    assignQuiz: builder.mutation<
      AssignTaskManagerQuizResponse,
      { payload: FormData; quizId: string }
    >({
      query: ({ payload, quizId }) => ({
        url: `quiz/assign-quiz?quizId=${quizId}`,
        method: "POST",
        body: payload,
      }),
    }),
    getQuizAssignedLevel: builder.query<
      GetAssingedTaskManagerQuizResponse,
      { quizId: string; assignQuizType: string }
    >({
      query: ({ quizId, assignQuizType }) => ({
        url: `quiz/get-parent-data?quizId=${quizId}&assignQuizType=${assignQuizType}`,
        method: "GET",
      }),
    }),
    getQuizSummary: builder.query<GetQuizSummaryResponse, { quizId: string }>({
      query: ({ quizId }) => ({
        url: `quiz/get?quizId=${quizId}`,
        method: "GET",
      }),
    }),
    getDuplicatedQuiz: builder.query<
      GetDuplicatedQuizResponse,
      { quizName: string; organizationId: string }
    >({
      query: ({ quizName, organizationId }) => ({
        url: `quiz/check-duplicate?quizName=${quizName}&organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    createUpdateAdditionalDetailsForm: builder.mutation<
      CreateUpdateAdditionalDetailsFormResponse,
      { quizId: string; payload: CreateUpdateAdditionalDetailsFormPayload }
    >({
      query: ({ quizId, payload }) => ({
        url: `quiz/add-additionalDetails?quizId=${quizId}`,
        method: "PATCH",
        body: payload,
      }),
    }),
    getSectionConfig: builder.query<
      any,
      { organizationId: string }
    >({
      query: ({ organizationId }) => ({
        url: `quiz/get-section-config?organizationId=${organizationId}`,
        method: "GET",
      }),
    }),
    createSectionConfig: builder.mutation<
      any,
      { organizationId: string; payload: any }
    >({
      query: ({ organizationId, payload }) => ({
        url: `quiz/section-config?organizationId=${organizationId}`,
        method: "POST",
        body: payload,
      }),
    }),
    updateSectionConfig: builder.mutation<
      any,
      { sectionConfigId: string; payload: any }
    >({
      query: ({ sectionConfigId, payload }) => ({
        url: `quiz/update-section-config?sectionConfigId=${sectionConfigId}`,
        method: "PUT",
        body: payload,
      }),
    }),
  }),
});

export const {
  useAddUpdateStartEndDateMutation,
  useAssignQuizMutation,
  useCreateQuizMutation,
  useLazyGetQuizByIDQuery,
  useUpdateQuizMutation,
  useUpdateQuizActionMutation,
  useGetQuizAssignedLevelQuery,
  useGetQuizSummaryQuery,
  useGetSuperTaskManagerQuizQuery,
  useLazyGetSuperTaskManagerQuizQuery,
  useLazyGetDuplicatedQuizQuery,
  useCreateUpdateAdditionalDetailsFormMutation,
  useLazyGetSectionConfigQuery,
  useCreateSectionConfigMutation,
  useUpdateSectionConfigMutation,
} = TaskManagerQuizApiSlice;
